import React from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { useAuth } from './context/AuthContext';
import AppLayout from './components/common/AppLayout';
import LoginPage from './pages/auth/LoginPage';
import Dashboard from './pages/dashboard/Dashboard';
import DashboardPage from './dashboard/DashboardPage';
import ManufacturingCommandCenter from './pages/dashboard/ManufacturingCommandCenter';
import Customer360Page from './pages/entity/Customer360Page';
import Supplier360Page from './pages/entity/Supplier360Page';
import Material360Page from './pages/entity/Material360Page';
import Machine360Page from './pages/entity/Machine360Page';
import FinancePage from './pages/finance/FinancePage';
import CrmPage from './pages/crm/CrmPage';
import HrPage from './pages/hr/HrPage';
import SalesPage from './pages/sales/SalesPage';
import ProcurementPage from './pages/procurement/ProcurementPage';
import AssetsPage from './pages/assets/AssetsPage';
import ProjectsPage from './pages/projects/ProjectsPage';
import CompliancePage from './pages/compliance/CompliancePage';
import EximPage from './pages/exim/EximPage';
import InventoryPage from './pages/inventory/InventoryPage';
import SettingsPage from './pages/settings/SettingsPage';
import LoadingSpinner from './components/common/LoadingSpinner';
import ModuleGuard from './components/common/ModuleGuard';
import InvoicePrintPage from './pages/sales/InvoicePrintPage';
import SSOLoginPage from './pages/auth/SSOLoginPage';
import VendorInvitePage from './pages/procurement/VendorInvitePage';
import { isRootDomain } from './utils/subdomain';

// Marketing surface — lazily loaded so authenticated users never download it.
const LandingPage = React.lazy(() => import('./pages/landing/LandingPage'));
const SignupPage = React.lazy(() => import('./pages/auth/SignupPage'));

const WorkflowsPage = React.lazy(() => import('./pages/workflows/WorkflowsPage'));
const IntegrationsPage = React.lazy(() => import('./pages/integrations/IntegrationsPage'));

const ReportsHubPage = React.lazy(() => import('./pages/reports/ReportsHubPage'));
const ReportViewPage = React.lazy(() => import('./pages/reports/ReportViewPage'));

const ActivitiesPage = React.lazy(() => import('./pages/dashboard/ActivitiesPage'));
const ActivityDashboardPage = React.lazy(() => import('./pages/admin/ActivityDashboardPage'));
const UserManagementPage = React.lazy(() => import('./pages/admin/UserManagementPage'));
const UserActivityDetailPage = React.lazy(() => import('./pages/admin/UserActivityDetailPage'));
const ModuleCataloguePage = React.lazy(() => import('./pages/admin/ModuleCataloguePage'));
const TreasuryPage = React.lazy(() => import('./pages/treasury/TreasuryPage'));
const ManufacturingPage = React.lazy(() => import('./pages/manufacturing/ManufacturingPage'));
const ScmPage = React.lazy(() => import('./pages/scm/ScmPage'));

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Public Route Component (redirect if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return isAuthenticated ? <Navigate to="/home" replace /> : <>{children}</>;
};

// Public marketing routes (landing, signup).
//
// Deliberately does NOT show a spinner while auth resolves, unlike PublicRoute.
// AuthContext.isLoading starts true, so mirroring PublicRoute would make every
// anonymous visitor watch a spinner before the hero paints — a real first-paint
// cost on the one page where first impressions matter. An authenticated user
// briefly seeing the marketing page before redirecting is harmless.
const MarketingRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  // Runs first and synchronously, so tenant hosts redirect with no landing flash.
  if (!isRootDomain()) {
    return <Navigate to="/login" replace />;
  }

  if (!isLoading && isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

// Unknown paths: send authenticated users to the app, everyone else to the
// landing. Previously every typo bounced anonymous visitors through
// /dashboard -> /login, a confusing double redirect.
const NotFoundRedirect: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return <Navigate to={isAuthenticated ? '/command-center' : '/'} replace />;
};

// Role-specific home: each role lands in the operational area they run, per the
// "role-specific home" requirement. Everyone can still reach every control room
// via the sidebar; this only picks the FIRST screen after login.
const RoleHome: React.FC = () => {
  const { user, isLoading } = useAuth();
  if (isLoading) return <LoadingSpinner />;
  const role = (user?.role || '').toUpperCase();
  const byRole: Record<string, string> = {
    FINANCE: '/finance', MANAGEMENT: '/finance',
    SALES: '/sales', PROCUREMENT: '/procurement', STORE: '/inventory',
    PRODUCTION: '/manufacturing', QUALITY: '/manufacturing/quality', MAINTENANCE: '/manufacturing/maintenance',
  };
  return <Navigate to={byRole[role] || '/command-center'} replace />;
};

// Suspense fallback for the landing chunk. Plain ink, not LoadingSpinner —
// the default spinner sits on a light background and produces a white flash
// before the dark hero.
const InkFallback: React.FC = () => (
  <Box sx={{ minHeight: '100vh', width: '100%', backgroundColor: '#05060f' }} />
);

// Alias for the literal /vendor-invite/:token shape — the backend's own
// buildInviteUrl() actually generates /vendor-onboarding/invite?token=...,
// so this just forwards a token-in-path link to the canonical query-string
// route without ever touching auth state.
const VendorInviteAlias: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  return <Navigate to={`/vendor-onboarding/invite${token ? `?token=${encodeURIComponent(token)}` : ''}`} replace />;
};

const App: React.FC = () => {
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%' }}>
      <Routes>
        {/* Public marketing routes */}
        <Route
          path="/"
          element={
            <MarketingRoute>
              <React.Suspense fallback={<InkFallback />}>
                <LandingPage />
              </React.Suspense>
            </MarketingRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <MarketingRoute>
              <React.Suspense fallback={<InkFallback />}>
                <SignupPage />
              </React.Suspense>
            </MarketingRoute>
          }
        />

        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />

        {/* SSO Login - Standalone route */}
        <Route path="/sso-login" element={<SSOLoginPage />} />

        {/* Vendor onboarding invite — public, unauthenticated. Matches the
            backend's buildInviteUrl() exactly (query-string token), plus a
            path-param alias that simply redirects into the canonical form. */}
        <Route path="/vendor-onboarding/invite" element={<VendorInvitePage />} />
        <Route path="/vendor-invite/:token" element={<VendorInviteAlias />} />

        {/* Print Routes - No AppLayout for clean printing */}
        <Route
          path="/sales/invoices/:id/print"
          element={
            <ProtectedRoute>
              <InvoicePrintPage />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes — pathless layout route.
            With no `path` of its own, children resolve against the nearest
            pathed ancestor (the root), so `dashboard` still matches
            /dashboard exactly as before. The former `index` route is gone:
            inside a pathless layout it would still match "/" and compete
            with the landing route above. */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="home" element={<RoleHome />} />
          <Route path="command-center" element={<ManufacturingCommandCenter />} />
          <Route path="entity/customer/:id" element={<Customer360Page />} />
          <Route path="entity/supplier/:id" element={<Supplier360Page />} />
          <Route path="entity/material/:id" element={<Material360Page />} />
          <Route path="entity/machine/:id" element={<Machine360Page />} />
          <Route path="dashboard" element={<DashboardPage />} />
          {/* The previous hand-written dashboard, kept reachable while the
              widget system beds in. */}
          <Route path="dashboard/classic" element={<Dashboard />} />
          <Route path="dashboard/activities" element={
            <React.Suspense fallback={<LoadingSpinner />}>
              <ActivitiesPage />
            </React.Suspense>
          } />
          <Route path="admin/dashboard" element={
            <React.Suspense fallback={<LoadingSpinner />}>
              <ActivityDashboardPage />
            </React.Suspense>
          } />
          <Route path="admin/users" element={
            <React.Suspense fallback={<LoadingSpinner />}>
              <UserManagementPage />
            </React.Suspense>
          } />
          <Route path="admin/users/:id" element={
            <React.Suspense fallback={<LoadingSpinner />}>
              <UserActivityDetailPage />
            </React.Suspense>
          } />
          {/* Module marketplace — core platform tooling, no ModuleGuard. */}
          <Route path="admin/modules" element={
            <React.Suspense fallback={<LoadingSpinner />}>
              <ModuleCataloguePage />
            </React.Suspense>
          } />
          <Route path="reports" element={
            <React.Suspense fallback={<LoadingSpinner />}>
              <ReportsHubPage />
            </React.Suspense>
          } />
          <Route path="reports/:id" element={
            <React.Suspense fallback={<LoadingSpinner />}>
              <ReportViewPage />
            </React.Suspense>
          } />
          {/* Finance is core (always on) — no ModuleGuard. Every other module
              route is gated: a tenant without the entitlement gets the locked
              panel instead of a page full of 403 tiles. */}
          <Route path="finance/*" element={<FinancePage />} />
          <Route path="crm/*" element={<ModuleGuard module="sales_crm"><CrmPage /></ModuleGuard>} />
          <Route path="hr/*" element={<ModuleGuard module="hr_payroll"><HrPage /></ModuleGuard>} />
          <Route path="sales/*" element={<ModuleGuard module="sales_crm"><SalesPage /></ModuleGuard>} />
          <Route path="procurement/*" element={<ModuleGuard module="procurement"><ProcurementPage /></ModuleGuard>} />
          <Route path="assets/*" element={<ModuleGuard module="fixed_assets"><AssetsPage /></ModuleGuard>} />
          <Route path="treasury/*" element={
            <ModuleGuard module="treasury">
              <React.Suspense fallback={<LoadingSpinner />}>
                <TreasuryPage />
              </React.Suspense>
            </ModuleGuard>
          } />
          <Route path="projects/*" element={<ModuleGuard module="projects"><ProjectsPage /></ModuleGuard>} />
          <Route path="compliance/*" element={<ModuleGuard module="compliance"><CompliancePage /></ModuleGuard>} />
          <Route path="exim/*" element={<ModuleGuard module="exim"><EximPage /></ModuleGuard>} />
          <Route path="inventory/*" element={<ModuleGuard module="inventory"><InventoryPage /></ModuleGuard>} />
          <Route path="scm/*" element={
            <ModuleGuard module="inventory">
              <React.Suspense fallback={<LoadingSpinner />}>
                <ScmPage />
              </React.Suspense>
            </ModuleGuard>
          } />
          <Route path="manufacturing/*" element={
            <ModuleGuard module="manufacturing">
              <React.Suspense fallback={<LoadingSpinner />}>
                <ManufacturingPage />
              </React.Suspense>
            </ModuleGuard>
          } />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="workflows/*" element={
            <ModuleGuard module="workflows">
              <React.Suspense fallback={<LoadingSpinner />}>
                <WorkflowsPage />
              </React.Suspense>
            </ModuleGuard>
          } />
          <Route path="integrations/*" element={
            <ModuleGuard module="integrations">
              <React.Suspense fallback={<LoadingSpinner />}>
                <IntegrationsPage />
              </React.Suspense>
            </ModuleGuard>
          } />
        </Route>

        {/* Catch all route */}
        <Route path="*" element={<NotFoundRedirect />} />
      </Routes>
    </Box>
  );
};

export default App;